// Вход на сайте: Telegram через бота (опрос раз в 2 с), код на почту, VK ID.
// В мини-приложении Telegram экран не нужен — вход по initData.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../platform/api.ts';
import { APP_URL } from '../platform/config.ts';
import { openTelegramLink } from '../platform/telegram.ts';
import { track } from '../platform/track.ts';
import { useMe } from './state.tsx';
import { Screen, Busy, toast } from './ui.tsx';
import { t, errText } from '../i18n/index.ts';

export function useTgPoll(onDone: (r: any) => void) {
  const [wait, setWait] = useState<{ nonce: string; link: string } | null>(null);
  const timer = useRef<number>();
  useEffect(() => {
    if (!wait) return;
    const tick = async () => {
      try {
        const r = await api('auth_tg_poll', { nonce: wait.nonce });
        if (r.pending) { timer.current = window.setTimeout(tick, 2000); return; }
        setWait(null);
        onDone(r);
      } catch (e) { setWait(null); toast(errText(e)); }
    };
    timer.current = window.setTimeout(tick, 2000);
    return () => clearTimeout(timer.current);
  }, [wait, onDone]);
  return { wait, start: (w: { nonce: string; link: string }) => { setWait(w); openTelegramLink(w.link); } };
}

export function Login() {
  const { me, reload } = useMe();
  const nav = useNavigate();
  const [cfg, setCfg] = useState<{ telegram: boolean; email: boolean; vk: boolean } | null>(null);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const done = async () => { await reload(); nav('/', { replace: true }); };
  const tgPoll = useTgPoll(done);

  useEffect(() => { track('login_open'); api('auth_config').then(setCfg).catch(e => toast(errText(e))); }, []);
  useEffect(() => { if (me && !me.profile.is_guest) nav('/', { replace: true }); }, [me, nav]);

  return (
    <Screen title={t('login.title')} back="/">
      <p className="muted">{t('login.subtitle')}</p>

      {cfg?.telegram && (
        <section className="card">
          {tgPoll.wait ? (
            <>
              <p><span className="spin" /> {t('login.tgWaiting')}</p>
              <button type="button" className="btn ghost" onClick={() => openTelegramLink(tgPoll.wait!.link)}>{t('login.tgOpenAgain')}</button>
            </>
          ) : (
            <Busy className="btn tg" onClick={async () => {
              track('login_method', { method: 'telegram' });
              try { tgPoll.start(await api('auth_tg_start')); } catch (e) { toast(errText(e)); }
            }}>{t('login.telegram')}</Busy>
          )}
        </section>
      )}

      {cfg?.email && (
        <section className="card">
          <h2>{t('login.email')}</h2>
          {!sent ? (
            <form onSubmit={e => e.preventDefault()}>
              <input type="email" inputMode="email" autoComplete="email" placeholder={t('login.emailPlaceholder')} value={email}
                     onChange={e => setEmail(e.target.value)} aria-label={t('login.emailPlaceholder')} />
              <Busy onClick={async () => {
                track('login_method', { method: 'email' });
                try { await api('auth_email_start', { email }); setSent(true); } catch (e) { toast(errText(e)); }
              }}>{t('login.sendCode')}</Busy>
            </form>
          ) : (
            <form onSubmit={e => e.preventDefault()}>
              <p className="muted">{t('login.codeSent', { email })}</p>
              <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder={t('login.codePlaceholder')} value={code}
                     onChange={e => setCode(e.target.value.replace(/\D/g, ''))} aria-label={t('login.codePlaceholder')} />
              <Busy onClick={async () => {
                try { await api('auth_email_verify', { email, code }); await done(); } catch (e) { toast(errText(e)); }
              }}>{t('login.signIn')}</Busy>
              <button type="button" className="link" onClick={() => { setSent(false); setCode(''); }}>{t('login.otherEmail')}</button>
            </form>
          )}
          <p className="small muted">{t('login.privacy')}</p>
        </section>
      )}

      {cfg?.vk && (
        <section className="card">
          <Busy className="btn vk" onClick={async () => {
            track('login_method', { method: 'vk' });
            try { location.href = (await api('auth_vk_start', { redirect_uri: APP_URL })).url; } catch (e) { toast(errText(e)); }
          }}>{t('login.vk')}</Busy>
        </section>
      )}
    </Screen>
  );
}
